<script lang="ts">
  import type { DrillCell, MetricTapRow, StandardTapRow } from './data';

  export let variant: 'standard' | 'metric' = 'standard';
  export let filter = '';

  export let standardRows: StandardTapRow[] = [];
  export let metricRows: MetricTapRow[] = [];

  function rowMatchesStandard(row: StandardTapRow, q: string): boolean {
    const hay = [
      row.screwSize,
      row.majorDiameter,
      String(row.tpi),
      row.minorDiameter,
      row.tap75.drill,
      row.tap75.decimal,
      row.tap50.drill,
      row.tap50.decimal,
      row.closeFit.drill,
      row.closeFit.decimal,
      row.freeFit.drill,
      row.freeFit.decimal,
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  }

  function rowMatchesMetric(row: MetricTapRow, q: string): boolean {
    const hay = [
      row.screwSize,
      row.pitch,
      row.tap75.drill,
      row.tap75.decimal,
      row.tap50.drill,
      row.tap50.decimal,
      row.closeFit.drill,
      row.closeFit.decimal,
      row.freeFit.drill,
      row.freeFit.decimal,
    ]
      .join(' ')
      .toLowerCase();
    return hay.includes(q);
  }

  function groupFilterStandard(rows: StandardTapRow[], q: string): StandardTapRow[] {
    if (!q) return rows;
    const matching = new Set<number>();
    rows.forEach((row, i) => {
      if (rowMatchesStandard(row, q)) matching.add(i);
    });
    const groups: number[][] = [];
    let current: number[] = [];
    rows.forEach((row, i) => {
      if (row.screwSize) {
        if (current.length) groups.push(current);
        current = [i];
      } else if (current.length) {
        current.push(i);
      }
    });
    if (current.length) groups.push(current);
    const keep = new Set<number>();
    for (const g of groups) {
      if (g.some((i) => matching.has(i))) {
        for (const i of g) keep.add(i);
      }
    }
    return rows.filter((_, i) => keep.has(i));
  }

  function groupFilterMetric(rows: MetricTapRow[], q: string): MetricTapRow[] {
    if (!q) return rows;
    const matching = new Set<number>();
    rows.forEach((row, i) => {
      if (rowMatchesMetric(row, q)) matching.add(i);
    });
    const groups: number[][] = [];
    let current: number[] = [];
    rows.forEach((row, i) => {
      if (row.screwSize) {
        if (current.length) groups.push(current);
        current = [i];
      } else if (current.length) {
        current.push(i);
      }
    });
    if (current.length) groups.push(current);
    const keep = new Set<number>();
    for (const g of groups) {
      if (g.some((i) => matching.has(i))) {
        for (const i of g) keep.add(i);
      }
    }
    return rows.filter((_, i) => keep.has(i));
  }

  function screwRowspans<T extends { screwSize: string }>(rows: T[]): (number | null)[] {
    const spans: (number | null)[] = new Array(rows.length).fill(null);
    let i = 0;
    while (i < rows.length) {
      let j = i;
      if (rows[i].screwSize) {
        while (j + 1 < rows.length && !rows[j + 1].screwSize) j++;
        spans[i] = j - i + 1;
        i = j + 1;
      } else {
        i++;
      }
    }
    return spans;
  }

  function majorRowspans(rows: StandardTapRow[]): (number | null)[] {
    const spans: (number | null)[] = new Array(rows.length).fill(null);
    let i = 0;
    while (i < rows.length) {
      if (rows[i].majorDiameter) {
        let j = i;
        while (j + 1 < rows.length && !rows[j + 1].majorDiameter) j++;
        spans[i] = j - i + 1;
        i = j + 1;
      } else {
        i++;
      }
    }
    return spans;
  }

  function hasDrill(cell: DrillCell): boolean {
    return Boolean(cell.drill || cell.decimal);
  }

  function screwGroups<T extends { screwSize: string }>(rows: T[]): number[][] {
    const groups: number[][] = [];
    let current: number[] = [];
    rows.forEach((row, i) => {
      if (row.screwSize) {
        if (current.length) groups.push(current);
        current = [i];
      } else if (current.length) {
        current.push(i);
      }
    });
    if (current.length) groups.push(current);
    return groups;
  }

  /** One merged clearance block per screw-size group (matches PDF). */
  function clearanceRowspans<T extends { screwSize: string; closeFit: DrillCell; freeFit: DrillCell }>(
    rows: T[],
    field: 'closeFit' | 'freeFit',
  ): (number | null)[] {
    const spans: (number | null)[] = new Array(rows.length).fill(null);
    for (const group of screwGroups(rows)) {
      let anchor = -1;
      for (const i of group) {
        if (hasDrill(rows[i][field])) {
          anchor = i;
          break;
        }
      }
      if (anchor >= 0) {
        spans[anchor] = group.length;
      }
    }
    return spans;
  }

  $: q = filter.trim().toLowerCase();
  $: visibleStandard =
    variant === 'standard' ? groupFilterStandard(standardRows, q) : [];
  $: visibleMetric = variant === 'metric' ? groupFilterMetric(metricRows, q) : [];
  $: screwSpansStd = screwRowspans(visibleStandard);
  $: majorSpansStd = majorRowspans(visibleStandard);
  $: closeSpansStd = clearanceRowspans(visibleStandard, 'closeFit');
  $: freeSpansStd = clearanceRowspans(visibleStandard, 'freeFit');
  $: screwSpansMet = screwRowspans(visibleMetric);
  $: closeSpansMet = clearanceRowspans(visibleMetric, 'closeFit');
  $: freeSpansMet = clearanceRowspans(visibleMetric, 'freeFit');

  /** Row index → screw-size group id (for merged-cell hover). */
  function rowGroupIds<T extends { screwSize: string }>(rows: T[]): number[] {
    const ids: number[] = [];
    let groupId = -1;
    for (let i = 0; i < rows.length; i++) {
      if (rows[i].screwSize) groupId++;
      ids[i] = groupId;
    }
    return ids;
  }

  $: groupIdsStd = rowGroupIds(visibleStandard);
  $: groupIdsMet = rowGroupIds(visibleMetric);

  let hoveredGroupId: number | null = null;

  $: variant, filter, (hoveredGroupId = null);

  function setHoveredFromRow(tr: HTMLElement) {
    const id = Number(tr.dataset.groupId);
    hoveredGroupId = Number.isNaN(id) ? null : id;
  }

  function onRowMouseenter(e: MouseEvent) {
    setHoveredFromRow(e.currentTarget as HTMLElement);
  }

  function onRowFocus(e: FocusEvent) {
    setHoveredFromRow(e.currentTarget as HTMLElement);
  }

  function clearHoveredIfLeavingGroup(e: MouseEvent | FocusEvent, groupId: number) {
    const related = e.relatedTarget;
    if (related instanceof HTMLElement) {
      const relatedTr = related.closest<HTMLElement>('tr[data-group-id]');
      if (relatedTr && Number(relatedTr.dataset.groupId) === groupId) return;
    }
    if (hoveredGroupId === groupId) hoveredGroupId = null;
  }
</script>

<div class="table-scroll">
  {#if variant === 'standard'}
    <table class="table table-sm tap-table mb-0">
      <thead>
        <tr class="group-header">
          <th colspan="4" class="border-end">Thread</th>
          <th colspan="4" class="border-end text-center">Tap drill</th>
          <th colspan="4" class="text-center">Clearance drill</th>
        </tr>
        <tr>
          <th>Screw</th>
          <th>Major dia.</th>
          <th>TPI</th>
          <th class="border-end">Minor dia.</th>
          <th colspan="2">75% (Al, brass, plastic)</th>
          <th colspan="2" class="border-end">50% (steel, stainless)</th>
          <th colspan="2">Close fit</th>
          <th colspan="2">Free fit</th>
        </tr>
        <tr class="subhead">
          <th colspan="4" class="border-end"></th>
          <th>Drill</th>
          <th>Dec. eq.</th>
          <th>Drill</th>
          <th class="border-end">Dec. eq.</th>
          <th>Drill</th>
          <th>Dec. eq.</th>
          <th>Drill</th>
          <th>Dec. eq.</th>
        </tr>
      </thead>
      <tbody>
        {#each visibleStandard as row, i}
          <tr
            data-group-id={groupIdsStd[i]}
            tabindex={row.screwSize !== '' ? 0 : undefined}
            class:group-start={row.screwSize !== ''}
            class:group-hovered={hoveredGroupId === groupIdsStd[i]}
            on:mouseenter={onRowMouseenter}
            on:mouseleave={(e) => clearHoveredIfLeavingGroup(e, groupIdsStd[i])}
            on:focus={onRowFocus}
            on:blur={(e) => clearHoveredIfLeavingGroup(e, groupIdsStd[i])}
          >
            {#if screwSpansStd[i]}
              <th scope="row" rowspan={screwSpansStd[i]} class="screw-cell">{row.screwSize}</th>
            {/if}
            {#if majorSpansStd[i]}
              <td rowspan={majorSpansStd[i]}>{row.majorDiameter}</td>
            {/if}
            <td class="mono">{row.tpi !== '' ? row.tpi : '—'}</td>
            <td class="mono border-end">{row.minorDiameter || '—'}</td>
            <td class="mono">{row.tap75.drill || '—'}</td>
            <td class="mono text-muted">{row.tap75.decimal || ''}</td>
            <td class="mono">{row.tap50.drill || '—'}</td>
            <td class="mono text-muted border-end">{row.tap50.decimal || ''}</td>
            {#if closeSpansStd[i]}
              <td class="mono clearance-cell clearance-start" rowspan={closeSpansStd[i]}>
                {row.closeFit.drill || '—'}
              </td>
              <td class="mono text-muted clearance-cell" rowspan={closeSpansStd[i]}>
                {row.closeFit.decimal || ''}
              </td>
            {/if}
            {#if freeSpansStd[i]}
              <td class="mono clearance-cell" rowspan={freeSpansStd[i]}>
                {row.freeFit.drill || '—'}
              </td>
              <td class="mono text-muted clearance-cell" rowspan={freeSpansStd[i]}>
                {row.freeFit.decimal || ''}
              </td>
            {/if}
          </tr>
        {:else}
          <tr>
            <td colspan="12" class="text-muted text-center py-4">No matching sizes.</td>
          </tr>
        {/each}
      </tbody>
    </table>
  {:else}
    <table class="table table-sm tap-table mb-0">
      <thead>
        <tr class="group-header">
          <th colspan="2" class="border-end">Thread</th>
          <th colspan="4" class="border-end text-center">Tap drill</th>
          <th colspan="4" class="text-center">Clearance drill</th>
        </tr>
        <tr>
          <th>Screw (mm)</th>
          <th class="border-end">Pitch (mm)</th>
          <th colspan="2">75% (Al, brass, plastic)</th>
          <th colspan="2" class="border-end">50% (steel, stainless)</th>
          <th colspan="2">Close fit</th>
          <th colspan="2">Free fit</th>
        </tr>
        <tr class="subhead">
          <th colspan="2" class="border-end"></th>
          <th>mm</th>
          <th>Am. drill</th>
          <th>mm</th>
          <th class="border-end">Am. drill</th>
          <th>mm</th>
          <th>Am. drill</th>
          <th>mm</th>
          <th>Am. drill</th>
        </tr>
      </thead>
      <tbody>
        {#each visibleMetric as row, i}
          <tr
            data-group-id={groupIdsMet[i]}
            tabindex={row.screwSize !== '' ? 0 : undefined}
            class:group-start={row.screwSize !== ''}
            class:group-hovered={hoveredGroupId === groupIdsMet[i]}
            on:mouseenter={onRowMouseenter}
            on:mouseleave={(e) => clearHoveredIfLeavingGroup(e, groupIdsMet[i])}
            on:focus={onRowFocus}
            on:blur={(e) => clearHoveredIfLeavingGroup(e, groupIdsMet[i])}
          >
            {#if screwSpansMet[i]}
              <th scope="row" rowspan={screwSpansMet[i]} class="screw-cell">{row.screwSize}</th>
            {/if}
            <td class="mono border-end">{row.pitch || '—'}</td>
            <td class="mono">{row.tap75.drill || '—'}</td>
            <td class="mono text-muted">{row.tap75.decimal || ''}</td>
            <td class="mono">{row.tap50.drill || '—'}</td>
            <td class="mono text-muted border-end">{row.tap50.decimal || ''}</td>
            {#if closeSpansMet[i]}
              <td class="mono clearance-cell clearance-start" rowspan={closeSpansMet[i]}>
                {row.closeFit.drill || '—'}
              </td>
              <td class="mono text-muted clearance-cell" rowspan={closeSpansMet[i]}>
                {row.closeFit.decimal || ''}
              </td>
            {/if}
            {#if freeSpansMet[i]}
              <td class="mono clearance-cell" rowspan={freeSpansMet[i]}>
                {row.freeFit.drill || '—'}
              </td>
              <td class="mono text-muted clearance-cell" rowspan={freeSpansMet[i]}>
                {row.freeFit.decimal || ''}
              </td>
            {/if}
          </tr>
        {:else}
          <tr>
            <td colspan="10" class="text-muted text-center py-4">No matching sizes.</td>
          </tr>
        {/each}
      </tbody>
    </table>
  {/if}
</div>

<style>
  .table-scroll {
    overflow-x: auto;
    max-height: calc(100dvh - 14rem);
    min-height: 32rem;
    border: 1px solid #dee2e6;
    border-radius: 0.375rem;
  }

  .tap-table {
    font-size: 0.8125rem;
  }

  .tap-table thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background: #f8f9fa;
    box-shadow: 0 1px 0 #dee2e6;
    white-space: nowrap;
    vertical-align: bottom;
  }

  .tap-table thead tr.group-header th {
    top: 0;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6c757d;
    padding-bottom: 0.25rem;
  }

  .tap-table thead tr:nth-child(2) th {
    top: 1.65rem;
  }

  .tap-table thead tr.subhead th {
    top: 3.35rem;
    font-weight: 500;
    font-size: 0.7rem;
    color: #868e96;
    padding-top: 0;
  }

  .tap-table .screw-cell {
    font-weight: 700;
    background: #fff;
    vertical-align: middle;
  }

  .tap-table .clearance-cell {
    vertical-align: middle;
    background: #fafbfc;
  }

  .tap-table .clearance-start {
    border-left: 2px solid #dee2e6;
  }

  .tap-table tr.group-start td,
  .tap-table tr.group-start th {
    border-top: 2px solid #e9ecef;
  }

  .tap-table tr.group-start:first-child td,
  .tap-table tr.group-start:first-child th {
    border-top: none;
  }

  .tap-table .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  }

  .tap-table tbody tr.group-hovered > th,
  .tap-table tbody tr.group-hovered > td {
    background-color: rgba(254, 209, 54, 0.22) !important;
  }

  .border-end {
    border-right: 1px solid #dee2e6 !important;
  }

  @media print {
    .table-scroll {
      max-height: none;
      overflow: visible;
      border: none;
    }
  }
</style>
